import Logger from "@/helpers/logger";
import billingApi from "./billing.api";
import store from "@/store";
import { PRODUCTS, hasRenewableOrder, hasRenewableAddon } from "@/helpers/product";
import {
  getDomainDetailsFromCurrentPlan,
  getMailSuiteDetailsFromCurrentPlan,
  getSiteDetailsFromCurrentPlan,
  processPlansWithPricingConfig,
  processRenewalCharge,
} from "@/helpers/billingHelpers";
import { getBundleId, getDefaultCurrency, getDomainName, isNeoBrand } from "@/helpers";
import { isOrderLevelBillingEnabled } from "@/helpers/bundle-billing.helper";
import { isPrepaidCard } from "@/helpers/stripe";
import {
  createRenewOrdersOLBPayload,
  createReactivateOrderPayload,
  createRenewBundlePayload,
} from "@/helpers/payment/bundlePaymentHelpers";

class BillingService {
  /**
   * It is necessary to pass domainName here because when a user logs in, the login response contains the primaryDomain.
   * We log the user into that account and find the bundle id from the bundles array in the login response.
   * However, the bundles list is limited to 100 items, so if the primary domain is not found in that list, everything fails and the user cannot use the control panel.
   * By passing domainName to getCurrentPlan, we ensure the backend can return the correct bundle id for that domain, so all further API calls that require the bundle id will work reliably.
   * The backend team has been informed about this limitation.
   */
  async getCurrentPlan() {
    try {
      const data = await billingApi.getCurrentPlan({
        domainName: getDomainName(),
      });

      // A missing body means the request failed even after retries (network/timeout is
      // resolved as undefined by the response interceptor) - fail loudly instead of
      // building an empty plan.
      if (!data) throw new Error("Empty current plan response");

      const payload = {
        ...data,
        // at present it is possible that currency can be empty in current plan
        // in such case default/USD current should be used
        currency: data.currency || getDefaultCurrency(),
        ...getMailSuiteDetailsFromCurrentPlan(data),
        ...getSiteDetailsFromCurrentPlan(data),
        ...getDomainDetailsFromCurrentPlan(data),
      };

      // Fetch renewal charge (calculate-charge) off the back of the current plan and
      // await it: the billing/subscription screens read the renewal amounts from it, so
      // if it fails even after retries we reject the whole current-plan load rather than
      // rendering an inconsistent (all-zero) billing state.
      await this.getRenewalCharge(payload);

      return Promise.resolve(payload);
    } catch (e) {
      Logger.error("`getCurrentPlan` service", e);
      return Promise.reject(e);
    }
  }

  /**
   * Fetches the renewal charge for the current bundle via the calculate-charge
   * (RenewBundle for BLB, RenewOrderManualOLB for OLB) API and stores a processed, lookup-friendly version in the store
   * (`renewalChargeDetails`). This is the source of truth for renewal amounts and
   * pricing on the billing & subscription screens, replacing the renewal fields that
   * used to come from the current-plan API.
   *
   * Renewal quotes are skipped for partner-managed billing or when no renewable
   * order or addon exists. The underlying request retries on transient
   * failures; if it still fails, this rejects so the caller (getCurrentPlan) can fail
   * loudly instead of leaving the billing screens in an inconsistent state.
   *
   * @param {Object} [currentPlan] - Current plan payload; defaults to the store value.
   * @returns {Promise<Object>} The processed renewal charge details.
   * @throws If the calculate-charge request fails after retries.
   */
  async getRenewalCharge(currentPlan = store.getters.currentPlanDetails) {
    // Partner-managed bundles do not need the app-load renewal quote. Only an
    // explicit false disables it, preserving compatibility with older responses.
    if (currentPlan?.billingManagedByUs === false) {
      store.dispatch("renewalChargeDetails", {});
      return {};
    }

    const bundleId = currentPlan?.bundleId || getBundleId();
    const isOLB = isOrderLevelBillingEnabled(currentPlan);

    // Skip calculate-charge (and clear any stale data) when there is nothing to renew -
    // no renewable order (paid/trial and not cancelled) AND no renewable addon (renews on
    // expiry). A renewable addon can exist even when the base orders aren't renewable
    // (e.g. a paid addon on a free plan), so it must be checked too. In these cases a
    // RenewBundle call would fail and there is nothing to show - the UI renders "-"/"$0.00".
    // Never fatal for these cases.
    if (
      !bundleId ||
      (!isOLB && !hasRenewableOrder(currentPlan) && !hasRenewableAddon(currentPlan))
    ) {
      store.dispatch("renewalChargeDetails", {});
      return {};
    }

    try {
      const bundle = {
        bundleId,
        currency: currentPlan?.currency || getDefaultCurrency(),
      };
      const payload = isOLB
        ? createRenewOrdersOLBPayload({ orderDetails: currentPlan.orderDetails, bundle })
        : createRenewBundlePayload({
            noOfAccountsToRenew: currentPlan?.mailSuiteDetails?.noOfAccounts?.purchased || null,
            chargeCancelledOrders: false,
            bundle,
          });

      if (!payload) {
        store.dispatch("renewalChargeDetails", {});
        return {};
      }

      const data = await billingApi.calculateBundleCharges(payload);

      // Missing body => request failed even after retries (network/timeout resolves as
      // undefined) => treat as a failure rather than storing empty renewal amounts.
      if (!data) throw new Error("Empty renewal charge response");

      const renewalChargeDetails = processRenewalCharge(data);
      store.dispatch("renewalChargeDetails", renewalChargeDetails);
      return renewalChargeDetails;
    } catch (e) {
      // Preserve the last successful snapshot - do NOT clear here. On a mid-session
      // refresh failure the screen stays mounted (syncCurrentPlan only logs the error)
      // and the current-plan snapshot is likewise not re-committed, so clearing would
      // make the mixin render every renewal amount as 0 while the plan still shows the
      // products. Keeping the previous value keeps the two snapshots consistent.
      Logger.error("`getRenewalCharge` service", e);
      throw e;
    }
  }

  async getAllPlans(currency, options = {}) {
    try {
      const data = await billingApi.getAllPlans(currency);

      const plansGroupedByOrderType = data.plans.reduce((acc, order) => {
        // incase `orderType` missing (specifically for old data) then consider that as mail plan
        const { orderType = PRODUCTS.MAIL_SUITE } = order;
        acc[orderType] = acc[orderType] || [];
        acc[orderType].push(order);
        return acc;
      }, {});

      // Extract pricing config IDs from current plan if available
      const pricingConfigIds = options.pricingConfigIds || {};

      // Apply pricing configurations to plans if config IDs are provided
      let processedPlans = plansGroupedByOrderType;
      if (Object.keys(pricingConfigIds).length > 0) {
        try {
          const pricingConfigs = await billingApi.getAllPricingConfigs();
          processedPlans = processPlansWithPricingConfig(
            plansGroupedByOrderType,
            pricingConfigIds,
            pricingConfigs,
            getDomainName()
          );
        } catch (pricingError) {
          Logger.error("`getAllPricingConfigs` failed - treating as critical", pricingError);
          // Re-throw so that user gets logged out
          throw new Error(`Pricing config fetch failed: ${pricingError}`);
        }
      }

      const mailPlanList = processedPlans[PRODUCTS.MAIL_SUITE] || [];
      const sitePlanList = processedPlans[PRODUCTS.SITE] || [];
      const domainPlanList = processedPlans[PRODUCTS.DOMAIN] || [];

      store.dispatch("mail/plans", mailPlanList);
      store.dispatch("site/plans", sitePlanList);
      store.dispatch("domain/plans", domainPlanList);

      return {
        [PRODUCTS.MAIL_SUITE]: mailPlanList,
        [PRODUCTS.SITE]: sitePlanList,
      };
    } catch (e) {
      Logger.error("`getAllPlans` service", e);
      return Promise.reject(e);
    }
  }

  async getAllAddons() {
    try {
      const data = await billingApi.getAllAddons();
      return data;
    } catch (e) {
      Logger.error("`getAllAddons` service", e);
      return Promise.reject(e);
    }
  }

  async getCardDetails({ syncWithStore = true } = {}) {
    try {
      const data = await billingApi.getCardDetails();
      syncWithStore && store.dispatch("payment/savedCard", data);
      return data;
    } catch (err) {
      if (err.data?.code === "CARD_NOT_FOUND") {
        store.dispatch("payment/savedCard", null);
      }

      Logger.error("`getCardDetails` service", err);
      return Promise.resolve(null);
    }
  }

  checkPaymentType(action) {
    return billingApi.checkPaymentType(action);
  }

  async calculateBundleCharges(requestPayload) {
    try {
      const data = await billingApi.calculateBundleCharges(requestPayload);
      return data;
    } catch (e) {
      Logger.error("`calculateBundleCharges` service", e);
      return Promise.reject(e);
    }
  }

  async reactivateOrder({ orderId }) {
    try {
      const payload = createReactivateOrderPayload({
        orderId,
        bundle: { bundleId: getBundleId() },
      });
      return await billingApi.calculateBundleCharges(payload);
    } catch (e) {
      Logger.error("`reactivateOrder` service", e);
      return Promise.reject(e);
    }
  }

  renewPlan(autoRenew, otherParams = {}) {
    try {
      return billingApi.renewPlan({
        autoRenew,
        ...otherParams,
      });
    } catch (e) {
      Logger.error("`updateConfig - renewPlan` service", e);
      return Promise.reject(e);
    }
  }

  changeBillingCycle(renewalBillingCycle) {
    try {
      return billingApi.changeBillingCycle({
        renewalBillingCycle,
      });
    } catch (e) {
      Logger.error("`updateConfig - changeBillingCycle` service", e);
      return Promise.reject(e);
    }
  }

  changeBillingDate(customExpiryDate) {
    try {
      return billingApi.changeBillingDate({ customExpiryDate });
    } catch (e) {
      Logger.error("`changeBillingDate` service", e);
      return Promise.reject(e);
    }
  }

  getAddCardIntent() {
    try {
      return billingApi.getAddCardIntent();
    } catch (e) {
      Logger.error("`getAddCardIntent` service", e);
      return Promise.reject(e);
    }
  }

  async deleteCards(payload) {
    try {
      return await billingApi.deleteCards(payload);
    } catch (e) {
      Logger.error("`deleteCards` service", e);
      return Promise.reject(e);
    }
  }

  async deleteIfPrepaidCard(cardDetails) {
    const { funding, paymentMethodId } = cardDetails || {};
    const _isPrepaidCard = isPrepaidCard(funding);

    // if not prepaid card or not neo brand then return false
    if (!_isPrepaidCard || !isNeoBrand()) {
      return Promise.resolve({ prepaidCard: false, cardDeleted: false });
    }

    try {
      // delete the newly added prepaid card
      await billingService.deleteCards({
        paymentMethodIds: [paymentMethodId],
        bundleId: getBundleId(),
      });
      return Promise.resolve({
        prepaidCard: true,
        cardDeleted: true,
      });
    } catch (e) {
      // failed to delete the prepaid card
      // in this case if user refresh page then he can use this case for payment completion
      console.error("Failed to delete prepaid card", e);
      return Promise.resolve({
        prepaidCard: true,
        cardDeleted: false,
      });
    }
  }

  async getTaxDetails() {
    try {
      return await billingApi.getTaxDetails();
    } catch (e) {
      Logger.error("`getTaxDetails` service", e);
      return Promise.reject(e);
    }
  }

  async getBillingAddress() {
    try {
      return await billingApi.getBillingAddress();
    } catch (e) {
      Logger.error("`getBillingAddress` service", e);
      return Promise.reject(e);
    }
  }

  async setBillingAddress(payload) {
    try {
      return await billingApi.setBillingAddress(payload);
    } catch (e) {
      Logger.error("`setBillingAddress` service", e);
      return Promise.reject(e);
    }
  }

  async updateAddonConfig(payload) {
    try {
      return await billingApi.updateAddonConfig(payload);
    } catch (e) {
      Logger.error("`updateAddonConfig` service", e);
      return Promise.reject(e);
    }
  }

  async initBundlePayment(requestPayload) {
    try {
      const data = await billingApi.initBundlePayment(requestPayload);
      return data;
    } catch (e) {
      Logger.error("`initBundlePayment` service", e);
      return Promise.reject(e);
    }
  }

  async completeBundlePayment(requestPayload) {
    try {
      const data = await billingApi.completeBundlePayment(requestPayload);
      return data;
    } catch (e) {
      Logger.error("`completeBundlePayment` service", e);
      return Promise.reject(e);
    }
  }
}

const billingService = new BillingService();

export default billingService;
